'use client';

/**
 * Forgot password — ui-ux-pro-max gold/cream shell
 */
import React, { useState, useEffect, useRef } from 'react';
import { Form, Input, Button } from 'antd';
import {
  MailOutlined,
  ArrowLeftOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  CloseOutlined,
  ArrowRightOutlined,
} from '@ant-design/icons';
import Link from 'next/link';
import { useTheme } from '@/shared/theme';
import { AuthNavbar } from '@/domains/auth/components/AuthNavbar';
import '@/shared/theme/styles/login.css';
import styles from './AuthLayout.module.css';

export default function ForgotPasswordPage() {
  const { isDark } = useTheme();
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState('');
  const errorRef = useRef<HTMLDivElement>(null);


  useEffect(() => {
    if (!error) return;
    const t = window.setTimeout(() => errorRef.current?.focus(), 0);
    return () => window.clearTimeout(t);
  }, [error]);

  const handleSubmit = async (values: { email: string }) => {
    setIsLoading(true);
    setError('');
    try {
      const res = await fetch('/api/auth/password/reset', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ email: values.email.trim() }),
      });
      const data = (await res.json().catch(() => ({}))) as {
        success?: boolean;
        error?: string;
        message?: string;
        devResetUrl?: string;
      };

      if (res.status === 429) {
        setError(data.error || 'Too many requests. Please try again later.');
        return;
      }
      if (!res.ok && data.error && res.status === 400) {
        setError(data.error);
        return;
      }

      // Always show success for valid requests (anti-enumeration)
      setIsSubmitted(true);

      // Dev-only: log link if API returned it
      if (data.devResetUrl && process.env.NODE_ENV === 'development') {
        console.info('[forgot-password] devResetUrl:', data.devResetUrl);
      }
    } catch {
      setError('Network error. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div
      className={`login-page ${isDark ? 'dark' : 'light'} ${styles.page}`}
      data-theme={isDark ? 'dark' : 'light'}
    >
      <AuthNavbar />

      <div className={styles.body}>
        <div className={`${styles.formContainer} ${styles.formContainerNarrow}`}>
          <div className={styles.card}>
            <div className={styles.authBezel}>
              <span className={styles.authBezelLabel}>auth · password</span>
              <span className={styles.authBezelStatus}>reset</span>
            </div>
            <div className={styles.cardBody}>
            {isSubmitted ? (
              <>
                <header className={styles.formHeaderCenter}>
                  <div className={styles.heroIcon} aria-hidden>
                    <CheckCircleOutlined />
                  </div>
                  <h1 className={styles.formTitle}>Check your email</h1>
                  <p className={styles.formSubtitle}>
                    If an account exists for that address, we sent a one-time reset link.
                  </p>
                </header>

                <div className={styles.successAlert} role="status">
                  <CheckCircleOutlined aria-hidden />
                  <span>
                    Check your inbox (and spam). The link expires in one hour and can be used once.
                    In local development the link is also printed in the server logs.
                  </span>
                </div>

                <Button
                  type="primary"
                  block
                  className={styles.submitBtn}
                  onClick={() => setIsSubmitted(false)}
                >
                  Try another email
                </Button>

                <div className={styles.divider} role="separator">
                  <div className={styles.dividerLine} />
                  <span className={styles.dividerText}>or</span>
                  <div className={styles.dividerLine} />
                </div>

                <Link href="/auth/client-login" style={{ textDecoration: 'none' }}>
                  <Button
                    type="default"
                    block
                    className={styles.secondaryBtn}
                    icon={<ArrowLeftOutlined aria-hidden />}
                  >
                    Back to sign in
                  </Button>
                </Link>
              </>
            ) : (
              <>
                <header className={styles.formHeader}>
                  <h1 className={styles.formTitle}>Reset password</h1>
                  <p className={styles.formSubtitle}>
                    Enter the email on your client account. If it matches, we send a one-time reset
                    link.
                  </p>
                </header>

                {error && (
                  <div
                    ref={errorRef}
                    className={styles.errorAlert}
                    role="alert"
                    tabIndex={-1}
                    id="forgot-password-error"
                  >
                    <ExclamationCircleOutlined className={styles.errorIcon} aria-hidden />
                    <div className={styles.errorContent}>
                      <div className={styles.errorTitle}>Could not send reset</div>
                      <div className={styles.errorText}>{error}</div>
                    </div>
                    <Button
                      type="text"
                      size="small"
                      icon={<CloseOutlined />}
                      onClick={() => setError('')}
                      className={styles.dismissBtn}
                      aria-label="Dismiss error"
                    />
                  </div>
                )}

                <Form
                  name="forgot-password"
                  onFinish={handleSubmit}
                  layout="vertical"
                  requiredMark={false}
                  className={styles.formFields}
                  disabled={isLoading}
                  aria-busy={isLoading}
                >
                  <Form.Item
                    name="email"
                    htmlFor="forgot-password-email"
                    label={<span className={styles.fieldLabel}>Email address</span>}
                    rules={[
                      { required: true, message: 'Enter your email' },
                      { type: 'email', message: 'Enter a valid email' },
                    ]}
                    validateTrigger={['onBlur', 'onSubmit']}
                  >
                    <Input
                      prefix={<MailOutlined aria-hidden />}
                      type="email"
                      id="forgot-password-email"
                      name="email"
                      placeholder="you@company.com"
                      autoComplete="email"
                      autoCapitalize="none"
                      spellCheck={false}
                      inputMode="email"
                      aria-required="true"
                      aria-describedby={error ? 'forgot-password-error' : undefined}
                    />
                  </Form.Item>

                  <Button
                    type="primary"
                    htmlType="submit"
                    loading={isLoading}
                    block
                    icon={!isLoading ? <ArrowRightOutlined /> : undefined}
                    iconPlacement="end"
                    className={styles.submitBtn}
                  >
                    {isLoading ? 'Sending…' : 'Send reset link'}
                  </Button>
                </Form>

                <div className={styles.divider} role="separator">
                  <div className={styles.dividerLine} />
                  <span className={styles.dividerText}>or</span>
                  <div className={styles.dividerLine} />
                </div>

                <div className={styles.footerRow}>
                  Remember your password?{' '}
                  <Link href="/auth/client-login" className={styles.forgotLink} style={{ minHeight: 'auto', display: 'inline' }}>
                    Sign in
                  </Link>
                </div>
              </>
            )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
